"use client";

import type { ReactNode } from "react";
import Image from "next/image";
import { Star } from "lucide-react";
import type { Testimonial } from "@/lib/testimonials";
import { cn } from "@/lib/utils";
import { Accent, SectionHeader } from "@/components/ui/SectionHeader";

/** Heavy, settling ease used for entries and swaps across every variant */
export const EASE = [0.32, 0.72, 0, 1] as const;

/** "plain **highlighted** plain" -> spans, highlights in the accent colour */
export const renderQuote = (quote: string) =>
  quote.split("**").map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className="text-brand-accent">
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    ),
  );

export const Stars = ({ rating, className }: { rating: number; className?: string }) => (
  <span
    className={cn("flex items-center gap-0.5", className)}
    role="img"
    aria-label={`${rating} out of 5 stars`}
  >
    {Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={cn("size-3.5", i < rating ? "fill-brand-accent text-brand-accent" : "text-brand-ink/15")}
      />
    ))}
  </span>
);

const initials = (name: string) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

type HeadshotProps = {
  t: Testimonial;
  /** Rendered size in px (ignored when `fill`) */
  size?: number;
  /** Cover the nearest positioned parent */
  fill?: boolean;
  sizes?: string;
  className?: string;
};

/**
 * Stands in for a full-bleed portrait when there is no photo. Same footprint, same ground as
 * the card: a paper field with a faint mint glow, the person's initials drawn as a pine
 * outline, the company named in the corner. Anything layered on the portrait (the fade,
 * the result chip) still lines up.
 */
const PortraitFallback = ({ t, className }: { t: Testimonial; className?: string }) => (
  <span
    role="img"
    aria-label={`${t.name}, ${t.role} at ${t.client}`}
    className={cn("absolute inset-0 block overflow-hidden bg-brand-paper", className)}
  >
    <span
      aria-hidden
      className="absolute inset-0 bg-[radial-gradient(75%_65%_at_28%_18%,rgba(43,196,138,0.35),transparent_70%)]"
    />
    <span
      aria-hidden
      className="absolute inset-0 flex items-center justify-center font-heading text-[clamp(7rem,22vw,15rem)] leading-none text-transparent [-webkit-text-stroke:2px_rgba(11,122,85,0.55)] select-none"
    >
      {initials(t.name)}
    </span>
    <span aria-hidden className="absolute left-6 top-6 font-mono text-[11px] uppercase tracking-widest text-brand-ink/60">
      {t.client}
    </span>
  </span>
);

/** The person's photo, or a monogram until one exists */
export const Headshot = ({ t, size = 40, fill = false, sizes, className }: HeadshotProps) => {
  const alt = `${t.name}, ${t.role} at ${t.client}`;

  if (fill) {
    return t.avatar ? (
      <Image src={t.avatar} alt={alt} fill sizes={sizes} className={cn("object-cover", className)} />
    ) : (
      <PortraitFallback t={t} className={className} />
    );
  }

  return t.avatar ? (
    <Image
      src={t.avatar}
      alt={alt}
      width={size}
      height={size}
      sizes={sizes ?? `${size}px`}
      style={{ width: size, height: size }}
      className={cn("shrink-0 rounded-xl object-cover", className)}
    />
  ) : (
    <span
      aria-hidden
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      className={cn(
        "flex shrink-0 items-center justify-center rounded-xl border border-black/10 bg-brand-paper font-heading text-brand-pine",
        className,
      )}
    >
      {initials(t.name)}
    </span>
  );
};

/** Company logo, or an initial mark plus the name until a logo exists */
export const CompanyMark = ({ t, className }: { t: Testimonial; className?: string }) => (
  <span className={cn("inline-flex min-w-0 items-center gap-2.5", className)}>
    {t.logo ? (
      <Image src={t.logo} alt={t.client} width={120} height={28} className="h-5 w-auto" />
    ) : (
      <>
        <span
          aria-hidden
          className="flex size-6 shrink-0 items-center justify-center rounded-md border border-black/15 bg-black/[0.06] font-heading text-[11px] leading-none text-brand-ink/80"
        >
          {t.client[0]}
        </span>
        <span className="truncate text-sm text-brand-ink/80">{t.client}</span>
      </>
    )}
  </span>
);

/** The reviews opener: same slab title as the other sections */
export const SectionIntro = ({
  headingId,
  title,
  align = "left",
}: {
  headingId: string;
  /** Wrap the word to print in the accent colour with <Accent>. */
  title: ReactNode;
  align?: "left" | "center";
}) => (
  <SectionHeader
    headingId={headingId}
    title={title}
    size="lg"
    align={align}
    className="mb-12 md:mb-16"
  />
);

export { Accent };

/**
 * Every review that is not on screen right now, kept in the DOM with `hidden`.
 * The interactive variants show one review at a time, so without this a crawler only ever
 * reads the first one. display: none text is still indexed; it just is not visible or read aloud.
 */
export const ReviewIndex = ({ testimonials, exclude = [] }: { testimonials: Testimonial[]; exclude?: string[] }) => {
  const rest = testimonials.filter((t) => !exclude.includes(t.id));
  if (rest.length === 0) return null;
  return (
    <ul hidden>
      {rest.map((t) => (
        <li key={t.id}>
          <blockquote>
            {t.hook}. {t.quote.split("**").join("")}
          </blockquote>
          <p>
            {t.name}, {t.role}, {t.client}. {t.result.value} {t.result.label}.
          </p>
        </li>
      ))}
    </ul>
  );
};

/** Dev-only checks that every review still fits the anatomy */
export const auditTestimonials = (testimonials: Testimonial[]) => {
  for (const t of testimonials) {
    const sentences = t.quote.split(/[.!?](?:\s|$)/).filter((s) => s.trim()).length;
    if (sentences > 2) {
      console.warn(`[Testimonials] "${t.id}" quote has ${sentences} sentences, max is 2.`);
    }
    const words = t.hook.trim().split(/\s+/).length;
    if (words < 3 || words > 5) {
      console.warn(`[Testimonials] "${t.id}" hook has ${words} words, aim for 3 to 5.`);
    }
    if (t.result.value.length > 7) {
      console.warn(`[Testimonials] "${t.id}" result value is long; it prints large. Keep it under 8 characters.`);
    }
    for (const [field, text] of Object.entries({ hook: t.hook, quote: t.quote, label: t.result.label })) {
      if (/[–—]/.test(text)) {
        console.warn(`[Testimonials] "${t.id}" ${field} contains a dash character; use a period or comma.`);
      }
    }
  }
};
